import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Formik, Form } from 'formik';
import * as R from 'ramda';
import combineActions from '@app/utils/combineActions';
import getFilters from '@app/actions/filters/getFilters';
import connect from '@app/utils/connect';
import {
    AUTOMATED_COLLECTIONS,
    AUTOMATED_CAMPAIGNS,
} from '@app/constants/config';
import {
    Drawer,
    Input,
    Button,
    Typography,
    Collapse,
    Flex,
    Modal,
    Select,
} from '@app/ui';
import { useRightsholderAttribution } from '@app/components/hooks/useRightsholderAttribution';
import { Client } from '@app/types/api';
import { capitalize } from '@app/utils/capitalize';
import { paramsOptions, baseParamOf, makeRowKey } from './options';
import AutomatedParamsContainer from './AutomatedParamsContainer';
import { ResultingLogicPreview } from './AndOrLogic';
import { buildLabelMaps, buildPreviewItems } from './paramValueDescription';
import {
    buildAutomatedParametersDocument,
    documentToSeed,
    isVersion2Document,
    AutomatedParametersDocument,
    BuilderRow,
} from './automatedParametersDocument';
import waitFor from '@app/utils/waitFor';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

/** Strip undefined values so { start: 1000, end: undefined } matches { start: 1000 } when comparing params */
function stripUndefinedDeep(obj: unknown): unknown {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) return obj.map(stripUndefinedDeep);
    if (typeof obj === 'object') {
        return Object.fromEntries(
            Object.entries(obj)
                .filter(([, v]) => v !== undefined)
                .map(([k, v]) => [k, stripUndefinedDeep(v)]),
        );
    }
    return obj;
}

type LogicalOperator = 'AND' | 'OR';

type AutomatedParameter = {
    type: string;
    name: string;
    values: string[];
    // SP-8387: per-condition operator (AND/OR) joining this condition to the
    // previous one. Absent/null on the first condition and on legacy configs.
    logicalOperator?: LogicalOperator | null;
};

type AutomatedParamFormValues = {
    name: string;
    description?: string;
    allSelectedParams: {
        [key: string]: {
            [key: string]: any;
        };
    };
};

type AutomatedParamDrawerProps = {
    type: string;
    onClose: () => void;
    getFilters: () => void;
    title: string;
    id: string | number;
    isEditMode: boolean;
    client: {
        permissions: (string | number)[];
    };
    // injected via connect — resolves id values to labels in the summary preview
    filters?: any;
    allValues: any[];
    value: {
        name: string;
        description: string;
        // Legacy v1 flat list. Present for v1 entities; null on v2 reads.
        automatedParameters: AutomatedParameter[];
        // SP-8647: v2 nodes tree returned on read. Preferred over the flat list.
        automatedParametersDocument?: AutomatedParametersDocument | null;
        isUsingRha?: boolean | null;
    };
    create: (d: any) => void;
    update: (d: any) => void;
    archive: (id: string | number) => void;
    open: boolean;
};

const AutomatedParamDrawer = ({
    type,
    title,
    onClose,
    id,
    value,
    isEditMode,
    client,
    filters,
    getFilters,
    create,
    update,
    archive,
    allValues,
    open,
}: AutomatedParamDrawerProps) => {
    const { t } = useTranslation(['tools', 'common']);
    const {
        rightsholderAttribution,
        hasPermission: hasRhAttributionPermission,
    } = useRightsholderAttribution({
        client: client as unknown as Client,
    });
    const {
        name,
        description,
        automatedParameters,
        automatedParametersDocument,
    } = value || {};
    const [showInitialMessage, setShowInitialMessage] = useState(
        type === 'campaigns' && allValues?.length === 0,
    );
    const [formKey, setFormKey] = useState(0); // Add key to force form reset

    // Reset form when drawer opens/closes
    useEffect(() => {
        if (open) {
            // Reset form key when drawer opens to force fresh form state
            setFormKey(prev => prev + 1);
        }
    }, [open]);

    const _create =
        (entityType: string) =>
        async (values, replacementId = null) => {
            const { name, description, allSelectedParams } = values;

            // SP-8141/SP-8647: build one row per builder row, in on-screen order, so
            // repeated parameters (e.g. two Sponsors conditions) survive. The row key
            // ('sponsors#2') maps back to the clean param name for the API and lets the
            // serializer correlate rows with their group + per-row operator.
            const orderedRows: BuilderRow[] = [];

            activeParams.forEach(rowKey => {
                const selectedParamValues = allSelectedParams[rowKey];

                // Skip placeholder rows and functions
                if (
                    typeof rowKey !== 'string' ||
                    rowKey.startsWith('emptyParam') ||
                    typeof selectedParamValues === 'function'
                ) {
                    return;
                }

                const name = baseParamOf(rowKey);
                const paramOption = paramsOptions.find(po => po.value == name);
                if (!paramOption || !paramOption.type) return;

                const type = paramOption.type;
                const hasComparingValues =
                    type == 'numberRange' ||
                    type == 'dateRange' ||
                    type == 'floatRange';
                const hasUsernameObjectType = type == 'usernamesObject';
                const hasTextType = type == 'text';

                // More robust empty check
                if (
                    !selectedParamValues ||
                    (typeof selectedParamValues === 'object' &&
                        !Array.isArray(selectedParamValues) &&
                        Object.entries(selectedParamValues).length === 0) ||
                    (Array.isArray(selectedParamValues) &&
                        selectedParamValues.length === 0)
                ) {
                    return;
                }

                let vals = [];
                if (hasComparingValues) {
                    vals = [JSON.stringify(selectedParamValues)];
                } else if (hasUsernameObjectType) {
                    vals = selectedParamValues
                        .filter(
                            s => s.platformId && s.names && s.names.length > 0,
                        )
                        .map(JSON.stringify);
                } else if (hasTextType) {
                    vals = selectedParamValues;
                } else if (Array.isArray(selectedParamValues)) {
                    // Array values (from Select/TreeSelect components)
                    vals = selectedParamValues;
                } else {
                    // Object values (boolean maps like {1: true, 2: true})
                    vals = Object.entries(selectedParamValues).reduce(
                        (accValues, [p, shouldBeAdded]) => {
                            if (shouldBeAdded) {
                                if (name == 'rightsholderPlacements') {
                                    const [rh, placementIds] = p.split('-');
                                    placementIds
                                        .split(',')
                                        .forEach(pid =>
                                            accValues.push(`${rh}-${pid}`),
                                        );
                                } else {
                                    accValues.push(p);
                                }
                            }
                            return accValues;
                        },
                        [],
                    );
                }

                // Only add if we have valid vals
                if (
                    !vals ||
                    vals.length === 0 ||
                    vals.some(v => typeof v === 'function')
                ) {
                    return;
                }

                orderedRows.push({ rowKey, type, name, values: vals });
            });

            // SP-8647: send the v2 `automatedParametersDocument` (nodes tree). Explicit
            // single-level groups map to homogeneous `group` nodes; ungrouped rows are
            // bare `condition` nodes; both join left-to-right by the per-row operator.
            // When there are no rules we omit the document (v2 requires a non-empty
            // nodes array) and send an empty flat list, which the backend treats as
            // "not auto-collected".
            const automatedParametersDocument = buildAutomatedParametersDocument(
                orderedRows,
                { logicalOperators, groupAssignments },
            );
            const automatedParamsFields = automatedParametersDocument
                ? { automatedParametersDocument }
                : { automatedParameters: [] };

            let payload;

            switch (entityType) {
                case 'collections':
                    payload = {
                        action: 'create',
                        domainObject: {
                            name,
                            description,
                            isUsingRha:
                                hasRhAttributionPermission &&
                                rightsholderAttribution === 'league',
                        },
                        ...automatedParamsFields,
                        replacementCollectionId: replacementId,
                    };
                    break;

                case 'campaigns':
                    payload = {
                        action: 'create',
                        domainObject: {
                            name,
                            description,
                        },
                        ...automatedParamsFields,
                        replacementCampaignId: replacementId,
                    };
                    break;

                default:
                    break;
            }

            await create(payload);
        };

    // SP-8141: seed the builder from the persisted, ordered condition list. Each
    // condition becomes its own row keyed by a unique row key, so repeated
    // parameters (e.g. two Sponsors conditions across OR branches) round-trip on
    // edit. Values are keyed by row key; the per-condition AND/OR operator is
    // seeded too (absent on the first/legacy condition).
    const seeded = useMemo(() => {
        const asp: Record<string, any> = {};
        const rows: string[] = [];
        const ops: Record<string, LogicalOperator> = {};
        const grp: Record<string, string> = {};
        // Group-first model: bare top-level conditions from the document become their
        // own singleton groups so every seeded row lands inside a group.
        let soloSeq = 0;

        // SP-8647: prefer the v2 nodes tree (seeded back into flat rows tagged with
        // their bracket) when present; fall back to the legacy v1 flat list, which has
        // no groups.
        const seed = isVersion2Document(automatedParametersDocument)
            ? documentToSeed(automatedParametersDocument)
            : {
                  conditions: (Array.isArray(automatedParameters)
                      ? automatedParameters
                      : []) as AutomatedParameter[],
              };
        const sourceConditions = seed.conditions;

        if (!Array.isArray(sourceConditions)) {
            return {
                allSelectedParams: asp,
                activeParams: rows,
                logicalOperators: ops,
                groupAssignments: grp,
            };
        }

        const known = new Set(paramsOptions.map(p => p.value));

        sourceConditions.forEach((ap: AutomatedParameter & { groupId?: string }) => {
            const par = paramsOptions.find(p => p.value == ap?.name);
            if (!par || !known.has(ap.name) || (ap.values?.length ?? 0) === 0) {
                return; // Skip unknown / valueless conditions
            }

            const rowKey = makeRowKey(ap.name, rows);
            rows.push(rowKey);
            // Every row belongs to a group: grouped rows keep their bracket id; a bare
            // condition gets a fresh singleton group id (serializes back to a bare node).
            grp[rowKey] = ap.groupId ?? `sg-${++soloSeq}`;

            const { type } = par;
            if (
                type == 'dateRange' ||
                type == 'numberRange' ||
                type == 'floatRange'
            ) {
                const json = JSON.parse(ap.values[0]);
                if (typeof json != 'object') return;
                asp[rowKey] = Object.entries(json).reduce(
                    (accSub, [subKey, subValue]: [string, string]) => {
                        if (!subValue) {
                            accSub[subKey] = undefined;
                            return accSub;
                        }
                        if (type == 'dateRange') {
                            accSub[subKey] = new Date(subValue);
                        } else if (type == 'numberRange') {
                            accSub[subKey] = parseInt(subValue);
                        } else {
                            accSub[subKey] = parseFloat(subValue).toFixed(3);
                        }
                        return accSub;
                    },
                    {},
                );
            } else if (type == 'text') {
                asp[rowKey] = ap.values;
            } else if (type == 'usernamesObject') {
                asp[rowKey] = ap.values.map(v => JSON.parse(v));
            } else {
                asp[rowKey] = {};
                ap.values.forEach(el => {
                    asp[rowKey][el] = true;
                });
            }

            // First row carries no operator; AND-before-OR precedence is rebuilt
            // from the seeded order + per-row operators.
            if (
                rows.length > 1 &&
                (ap.logicalOperator === 'AND' || ap.logicalOperator === 'OR')
            ) {
                ops[rowKey] = ap.logicalOperator;
            }
        });

        return {
            allSelectedParams: asp,
            activeParams: rows,
            logicalOperators: ops,
            groupAssignments: grp,
        };
    }, [automatedParameters, automatedParametersDocument]);

    const allSelectedParams = seeded.allSelectedParams;
    const initialLogicalOperators = seeded.logicalOperators;
    const initialGroupAssignments = seeded.groupAssignments;

    const [activeParams, setActiveParams] = useState<string[]>([]);
    const [logicalOperators, setLogicalOperators] = useState<
        Record<string, LogicalOperator>
    >(initialLogicalOperators);
    // SP-8647: single-level grouping — row key → bracket id. A bracket is purely a
    // precedence group over a contiguous run of rows; each row keeps its own operator.
    const [groupAssignments, setGroupAssignments] = useState<
        Record<string, string>
    >(initialGroupAssignments);
    const groupIdSeq = useRef(0);
    // Monotonic counter for placeholder-row ids so a re-added condition never collides
    // with an existing `emptyParamN` that hasn't been activated yet.
    const emptyParamSeq = useRef(0);

    const setLogicalOperator = (paramName: string, op: LogicalOperator) =>
        setLogicalOperators(prev => ({ ...prev, [paramName]: op }));

    // SP-8647 group-first builder. Every row belongs to a group; a group is a
    // precedence bracket and each row keeps its own AND/OR to the previous sibling.

    // Add a new group seeded with one empty condition (appended after existing groups).
    const addGroup = () => {
        const groupId = `grp-${++groupIdSeq.current}`;
        const emptyId = `emptyParam${++emptyParamSeq.current}`;
        setActiveParams(prev => [...prev, emptyId]);
        setGroupAssignments(prev => ({ ...prev, [emptyId]: groupId }));
    };

    // Add an empty condition to an existing group, inserted right after that group's
    // last row so its rows stay contiguous (contiguity is how groups are rendered
    // and serialized).
    const addConditionToGroup = (groupId: string) => {
        const emptyId = `emptyParam${++emptyParamSeq.current}`;
        let lastIdx = -1;
        activeParams.forEach((rk, i) => {
            if (groupAssignments[rk] === groupId) lastIdx = i;
        });
        setActiveParams(prev => {
            const next = [...prev];
            if (lastIdx < 0) next.push(emptyId);
            else next.splice(lastIdx + 1, 0, emptyId);
            return next;
        });
        setGroupAssignments(prev => ({ ...prev, [emptyId]: groupId }));
    };

    // Remove a whole group (all its rows). Stale group/operator entries are pruned by
    // the effect that keeps grouping in sync with activeParams.
    const removeGroup = (groupId: string) => {
        const removed = new Set(
            activeParams.filter(rk => groupAssignments[rk] === groupId),
        );
        setActiveParams(prev => prev.filter(rk => !removed.has(rk)));
    };

    // Carry a row's group membership + operator across the emptyParam → chosen-param
    // key rename, so activating a placeholder keeps it inside its group.
    const onRowActivated = (oldKey: string, newKey: string) => {
        setGroupAssignments(prev => {
            if (prev[oldKey] === undefined) return prev;
            const next = { ...prev };
            next[newKey] = prev[oldKey];
            delete next[oldKey];
            return next;
        });
        setLogicalOperators(prev => {
            if (prev[oldKey] === undefined) return prev;
            const next = { ...prev };
            next[newKey] = prev[oldKey];
            delete next[oldKey];
            return next;
        });
    };

    // Update activeParams + operators + grouping when the seeded config changes. Rows
    // follow the persisted order so the saved logic renders as it was stored.
    useEffect(() => {
        setActiveParams(seeded.activeParams);
        setLogicalOperators(seeded.logicalOperators);
        setGroupAssignments(seeded.groupAssignments);
        groupIdSeq.current = 0;
    }, [seeded]);

    // Reset when the drawer opens in create mode — start with one group holding a
    // single empty condition (the group-first default).
    useEffect(() => {
        if (open && !isEditMode) {
            emptyParamSeq.current = 0;
            groupIdSeq.current = 0;
            const emptyId = `emptyParam${++emptyParamSeq.current}`;
            const groupId = `grp-${++groupIdSeq.current}`;
            setActiveParams([emptyId]);
            setLogicalOperators({});
            setGroupAssignments({ [emptyId]: groupId });
            setShowInitialMessage(
                type === 'campaigns' && allValues?.length === 0,
            );
        }
    }, [open, isEditMode, type, allValues]);

    // Keep grouping in sync with the rows: drop assignments for rows that no longer
    // exist. Single-condition groups are valid in the group-first model, so groups are
    // never auto-dissolved. Rows are only appended or removed (never reordered), so
    // grouped runs stay contiguous.
    useEffect(() => {
        setGroupAssignments(prev => {
            const present = new Set(activeParams);
            const next: Record<string, string> = {};
            let changed = false;
            Object.keys(prev).forEach(rk => {
                if (present.has(rk)) next[rk] = prev[rk];
                else changed = true;
            });
            return changed ? next : prev;
        });
    }, [activeParams]);

    const showAutomatedCollections =
        client.permissions.includes(AUTOMATED_COLLECTIONS) &&
        type == 'collections';

    const showAutomatedCampaigns =
        client.permissions.includes(AUTOMATED_CAMPAIGNS) && type == 'campaigns';

    // Check for empty rows (either empty string or emptyParam identifiers)
    const hasEmptyRow = activeParams.some(
        ap =>
            ap === '' ||
            (typeof ap === 'string' && ap.startsWith('emptyParam')),
    );

    const validate = (values: AutomatedParamFormValues) => {
        const errors: {
            name?: string;
            description?: string;
            allSelectedParams?: {
                [key: string]: string;
            };
        } = {};

        if (type === 'campaigns' && !values.name)
            errors.name = t('tools:automatedParams.validation.required');

        if (values.name.length <= 1)
            errors.name = t('tools:automatedParams.validation.nameMinLength');
        if (values.name.length > 50)
            errors.name = t('tools:automatedParams.validation.nameMaxLength');

        const nameField = type === 'campaigns' ? 'campaignName' : 'name';

        if (
            Array.isArray(allValues) &&
            allValues.findIndex(c => c[nameField] == values.name) > -1
        )
            errors.name = t('tools:automatedParams.validation.nameTaken');

        if (values.name.length > 50)
            if (!values.name)
                errors.name = t('tools:automatedParams.validation.required');

        if (values?.description?.length > 255)
            errors.description = t(
                'tools:automatedParams.validation.descMaxLength',
            );

        activeParams.forEach(param => {
            // Skip emptyParam identifiers - they shouldn't be validated
            if (typeof param === 'string' && param.startsWith('emptyParam')) {
                return;
            }

            // `param` is the row key (storage + error key); resolve the option and
            // display name from the base parameter value (SP-8141).
            const base = baseParamOf(param);
            const paramOption = paramsOptions.find(p => p.value == base);
            if (!paramOption) return; // Skip if param option not found

            const type = paramOption.type;
            const paramValues = values.allSelectedParams[param];

            // Handle text type (arrays)
            if (type === 'text') {
                if (
                    !paramValues ||
                    !Array.isArray(paramValues) ||
                    paramValues.length === 0
                ) {
                    if (!errors.allSelectedParams) {
                        errors.allSelectedParams = {};
                    }
                    const showName = t(
                        `tools:collections.parameterNames.${base}`,
                    ).toLowerCase();
                    errors.allSelectedParams[param] = t(
                        'tools:automatedParams.validation.selectOneOrMore',
                        { name: showName },
                    );
                }
                return;
            }

            // Handle usernamesObject type (array of objects)
            if (type === 'usernamesObject') {
                if (
                    !paramValues ||
                    !Array.isArray(paramValues) ||
                    paramValues.length === 0
                ) {
                    if (!errors.allSelectedParams) {
                        errors.allSelectedParams = {};
                    }
                    const showName = t(
                        `tools:collections.parameterNames.${base}`,
                    ).toLowerCase();
                    errors.allSelectedParams[param] = t(
                        'tools:automatedParams.validation.selectOneOrMore',
                        { name: showName },
                    );
                } else {
                    // Check if all entries have valid platformId and names
                    const hasValidEntries = paramValues.some(
                        (v: any) =>
                            v.platformId && v.names && v.names.length > 0,
                    );
                    if (!hasValidEntries) {
                        if (!errors.allSelectedParams) {
                            errors.allSelectedParams = {};
                        }
                        const showName = t(
                            `tools:collections.parameterNames.${base}`,
                        ).toLowerCase();
                        errors.allSelectedParams[param] = t(
                            'tools:automatedParams.validation.selectOneOrMore',
                            { name: showName },
                        );
                    }
                }
                return;
            }

            // Skip if paramValues is null, undefined, or not an object
            if (
                !paramValues ||
                typeof paramValues !== 'object' ||
                Array.isArray(paramValues)
            )
                return;

            const selected = Object.entries(paramValues).reduce(
                (acc, [k, v]) => {
                    if (v) acc.push(k);
                    return acc;
                },
                [],
            );

            if (selected.length == 0) {
                if (!errors.allSelectedParams) {
                    errors.allSelectedParams = {};
                }
                const showName = t(
                    `tools:collections.parameterNames.${base}`,
                ).toLowerCase();
                errors.allSelectedParams[param] = t(
                    'tools:automatedParams.validation.selectOneOrMore',
                    { name: showName },
                );
            }
        });

        return errors;
    };

    const entityName = useMemo(() => {
        const key =
            type === 'collections'
                ? 'tools:collections.title'
                : 'common:filters.campaigns';
        return t(key);
    }, [type, t]);

    // Singular form ("Collection"/"Campaign") for labels that read better in
    // the singular (entityName is the plural "Collections"/"Campaigns").
    const singularEntity = useMemo(() => entityName.slice(0, -1), [entityName]);

    // id→label maps so the pre-submit summary can show real values, matching the
    // in-drawer "resulting logic" preview.
    const labelMaps = useMemo(
        () => buildLabelMaps({ filters, client, entityType: type }),
        [filters, client, type],
    );

    // The grouped preview items backing the resulting-logic preview, built from the
    // current form values in row order. Shared with the in-drawer builder via the helper.
    const getPreviewItems = (formValues: AutomatedParamFormValues) =>
        buildPreviewItems({
            activeParams,
            allSelectedParams: formValues.allSelectedParams,
            logicalOperators,
            groupAssignments,
            paramsOptions,
            labelMaps,
        });

    // Read-only summary shown in the confirm-before-save dialog: a one-line
    // "your {entity} will capture posts matching these rules" + the preview.
    const renderSummary = (formValues: AutomatedParamFormValues) => (
        <div style={{ marginTop: 8 }}>
            {/* The description shares the toggle row (heading), so no dead left space. */}
            <ResultingLogicPreview
                conditions={[]}
                items={getPreviewItems(formValues)}
                title={t('tools:automatedParams.headers.resultingLogic', {
                    entity: singularEntity,
                })}
                emptyText={t('tools:automatedParams.messages.noConditionsYet')}
                hideChrome
                heading={t('tools:automatedParams.messages.captureSummary', {
                    entity: singularEntity,
                })}
            />
        </div>
    );

    const onCloseWithMessage = () => {
        const singleEntityName = entityName.slice(0, -1);
        const confirmationContent = (
            <Trans
                i18nKey="tools:automatedParams.modals.closeContent"
                values={{ entity: singleEntityName.toLowerCase() }}
            />
        );

        Modal.confirm({
            title: t('tools:automatedParams.modals.closeTitle', {
                entity: capitalize(singleEntityName),
            }),
            content: confirmationContent,
            icon: (
                <i
                    className="fa-solid fa-triangle-exclamation"
                    style={{
                        color: 'rgba(212, 15, 125, 1)',
                        marginRight: '1.1rem',
                        fontSize: '1.2rem',
                        marginTop: '4px',
                    }}
                />
            ),
            okText: t('tools:automatedParams.buttons.yesClose'),
            cancelText: (
                <span>
                    <i className="fa-regular fa-x -m-r-2" />
                    {t('common:cancel')}
                </span>
            ),
            zIndex: 2200,
            okButtonProps: {
                style: {
                    marginTop: '1rem',
                    backgroundColor: 'rgba(212, 15, 125, 1)',
                    borderColor: 'rgba(212, 15, 125, 1)',
                },
            },
            cancelButtonProps: {
                style: {
                    marginTop: '1rem',
                    backgroundColor: '#000',
                    borderColor: '#000',
                    color: '#fff',
                },
            },
            onOk: () => {
                // Reset form state when closing
                setFormKey(prev => prev + 1);
                onClose();
            },
            onCancel: () => {
                // User cancelled, do nothing
            },
        });
    };

    return (
        <Formik<AutomatedParamFormValues>
            key={formKey} // Force form reset when key changes
            initialValues={{
                name,
                description,
                allSelectedParams,
            }}
            validate={validate}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={async (values, actions) => {
                // Validate the form first - Formik will set errors if validation fails
                const validationErrors = await actions.validateForm();

                // If there are validation errors, stop submission and show errors
                if (Object.keys(validationErrors).length > 0) {
                    actions.setSubmitting(false);
                    return;
                }

                const initialNormalized = stripUndefinedDeep(
                    allSelectedParams ?? {},
                ) as Record<string, unknown>;
                const currentNormalized = stripUndefinedDeep(
                    values.allSelectedParams ?? {},
                ) as Record<string, unknown>;
                const hasChangedParams =
                    !R.equals(currentNormalized, initialNormalized) ||
                    !R.equals(logicalOperators, initialLogicalOperators) ||
                    !R.equals(groupAssignments, initialGroupAssignments);
                const hasChangedNameOrDescription =
                    values.name !== name || values?.description !== description;

                const shouldNotUpdate =
                    isEditMode &&
                    values.name == name &&
                    values?.description == description &&
                    !hasChangedParams;

                if (shouldNotUpdate) {
                    actions.setSubmitting(false);
                    return;
                }

                if (
                    isEditMode &&
                    (hasChangedParams || hasChangedNameOrDescription)
                ) {
                    const singleType = entityName.slice(0, -1);
                    const hasSummary = getPreviewItems(values).length > 0;
                    const confirmationContent = (
                        <>
                            <Trans
                                i18nKey="tools:automatedParams.modals.updateParamsContent"
                                values={{ entity: singleType.toLowerCase() }}
                            />
                            {hasSummary && renderSummary(values)}
                        </>
                    );

                    return new Promise(() => {
                        Modal.confirm({
                            title: t(
                                'tools:automatedParams.modals.updateParamsTitle',
                                {
                                    entity: capitalize(singleType),
                                },
                            ),
                            content: confirmationContent,
                            icon: (
                                <i
                                    className="fa-solid fa-triangle-exclamation"
                                    style={{
                                        color: 'rgba(212, 15, 125, 1)',
                                        marginRight: '1.1rem',
                                        fontSize: '1.2rem',
                                        marginTop: '4px',
                                    }}
                                />
                            ),
                            okText: t(
                                'tools:automatedParams.buttons.yesContinue',
                            ),
                            cancelText: (
                                <span>
                                    <i className="fa-regular fa-x -m-r-2" />
                                    {t('common:cancel')}
                                </span>
                            ),
                            zIndex: 2200,
                            okButtonProps: {
                                style: {
                                    marginTop: '1rem',
                                    backgroundColor: 'rgba(212, 15, 125, 1)',
                                    borderColor: 'rgba(212, 15, 125, 1)',
                                },
                            },
                            cancelButtonProps: {
                                style: {
                                    marginTop: '1rem',
                                    backgroundColor: '#000',
                                    borderColor: '#000',
                                    color: '#fff',
                                },
                            },
                            onOk: async () => {
                                try {
                                    if (isEditMode) {
                                        await archive(id);
                                        await waitFor(900);
                                    }
                                    await _create(type)(values, id);
                                    actions.setSubmitting(false);

                                    getFilters();
                                    onClose();
                                } catch (e) {
                                    actions.setSubmitting(false);
                                    actions.setStatus({
                                        msg: JSON.parse(e.message).message,
                                    });
                                }
                            },
                            onCancel: () => {
                                actions.setSubmitting(false);
                            },
                        });
                    });
                }

                const doCreate = async () => {
                    try {
                        await _create(type)(values, id);
                        actions.setSubmitting(false);
                        getFilters();
                        onClose();
                    } catch (e) {
                        actions.setSubmitting(false);
                        actions.setStatus({
                            msg: JSON.parse(e.message).message,
                        });
                    }
                };

                if (isEditMode && !hasChangedParams) {
                    try {
                        await update({
                            name: values.name,
                            description: values?.description,
                        });
                        actions.setSubmitting(false);
                        getFilters();
                        onClose();
                    } catch (e) {
                        actions.setSubmitting(false);
                        actions.setStatus({
                            msg: JSON.parse(e.message).message,
                        });
                    }
                    return;
                }

                // Create flow: when the new entity has rules, confirm with a
                // read-only summary of the resulting logic before saving (rules
                // can get complex). No rules → nothing to review, create directly.
                if (getPreviewItems(values).length > 0) {
                    actions.setSubmitting(false);
                    return new Promise(() => {
                        Modal.confirm({
                            title: t(
                                'tools:automatedParams.modals.confirmSummaryTitle',
                                { entity: capitalize(singularEntity) },
                            ),
                            content: renderSummary(values),
                            width: 640,
                            zIndex: 2200,
                            okText: t(
                                'tools:automatedParams.buttons.yesContinue',
                            ),
                            cancelText: (
                                <span>
                                    <i className="fa-regular fa-x -m-r-2" />
                                    {t('common:cancel')}
                                </span>
                            ),
                            onOk: doCreate,
                            onCancel: () => actions.setSubmitting(false),
                        });
                    });
                }

                await doCreate();
            }}
        >
            {({
                values,
                errors,
                status,
                handleChange,
                handleBlur,
                isSubmitting,
                setTouched,
                setValues,
                setErrors,
                submitForm,
            }) => {
                const hasChangedParams =
                    !R.equals(values.allSelectedParams, allSelectedParams) ||
                    !R.equals(logicalOperators, initialLogicalOperators) ||
                    !R.equals(groupAssignments, initialGroupAssignments);
                const shouldNotUpdate =
                    isEditMode &&
                    values.name == name &&
                    values?.description == description &&
                    !hasChangedParams;

                const disabledSubmit =
                    hasEmptyRow ||
                    isSubmitting ||
                    shouldNotUpdate ||
                    !values.name;

                const hasClearAll =
                    (values.name ||
                        values.description ||
                        !R.isEmpty(values.allSelectedParams)) &&
                    !isSubmitting;

                return (
                    <Drawer
                        title={title}
                        placement="right"
                        onClose={onCloseWithMessage}
                        open={open}
                        width={window.innerWidth * 0.76}
                        zIndex={2100}
                        extra={
                            <>
                                {showInitialMessage ? (
                                    <Button
                                        type="primary"
                                        onClick={() =>
                                            setShowInitialMessage(false)
                                        }
                                        data-test="continue-button"
                                    >
                                        {t(
                                            'tools:automatedParams.buttons.continue',
                                        )}
                                    </Button>
                                ) : (
                                    <Flex>
                                        {type === 'collections' ? (
                                            <Button
                                                className="-m-r-2"
                                                type="primary"
                                                color="default"
                                                variant="solid"
                                                data-test="clear-all-btn"
                                                onClick={() => {
                                                    setValues({
                                                        name: '',
                                                        description: '',
                                                        allSelectedParams: {},
                                                    });
                                                    setActiveParams([]);
                                                    setLogicalOperators({});
                                                    setGroupAssignments({});
                                                }}
                                                disabled={!hasClearAll}
                                                icon={
                                                    <i className="fa-regular fa-arrow-rotate-right -m-r-2" />
                                                }
                                            >
                                                {t(
                                                    'tools:automatedParams.buttons.clearAll',
                                                )}
                                            </Button>
                                        ) : null}
                                        <Button
                                            type="primary"
                                            onClick={submitForm}
                                            data-test="create-submit-btn"
                                            disabled={
                                                disabledSubmit || isSubmitting
                                            }
                                            icon={
                                                type === 'collections' ? (
                                                    <i className="fa-regular fa-images" />
                                                ) : undefined
                                            }
                                        >
                                            {`${
                                                isEditMode
                                                    ? t('common:buttons.edit')
                                                    : t('common:buttons.submit')
                                            } ${entityName}`}
                                        </Button>
                                    </Flex>
                                )}
                            </>
                        }
                    >
                        <Form>
                            {showInitialMessage && (
                                <div className="gds-text--header-md">
                                    <Title level={4}>
                                        {t(
                                            'tools:automatedParams.messages.noCampaigns',
                                        )}
                                    </Title>
                                    <Text>
                                        {t(
                                            'tools:automatedParams.messages.createNow',
                                            {
                                                entity: entityName,
                                            },
                                        )}
                                    </Text>
                                </div>
                            )}
                            {!showInitialMessage && (
                                <>
                                    <Collapse
                                        expandIconPosition="end"
                                        defaultActiveKey={['1']}
                                    >
                                        <Panel
                                            style={{ fontWeight: 600 }}
                                            header={t(
                                                'tools:automatedParams.headers.info',
                                                {
                                                    entity: singularEntity,
                                                },
                                            )}
                                            key="1"
                                        >
                                            <Flex vertical gap={16}>
                                                <Flex gap={12}>
                                                    <Flex vertical flex={1}>
                                                        <Typography.Text className="-filter-title">
                                                            {t(
                                                                'tools:automatedParams.headers.name',
                                                                {
                                                                    entity: singularEntity,
                                                                },
                                                            )}
                                                        </Typography.Text>
                                                        <Input
                                                            name="name"
                                                            placeholder={t(
                                                                'tools:automatedParams.placeholders.enterName',
                                                                {
                                                                    entity: singularEntity,
                                                                },
                                                            )}
                                                            onChange={
                                                                handleChange
                                                            }
                                                            onBlur={handleBlur}
                                                            value={values.name}
                                                            data-test="name"
                                                            status={
                                                                errors.name
                                                                    ? 'error'
                                                                    : ''
                                                            }
                                                        />
                                                        {errors.name && (
                                                            <Typography.Text
                                                                type="danger"
                                                                style={{
                                                                    fontSize:
                                                                        '14px',
                                                                    marginTop:
                                                                        '4px',
                                                                    display:
                                                                        'block',
                                                                }}
                                                            >
                                                                {errors.name}
                                                            </Typography.Text>
                                                        )}
                                                    </Flex>

                                                    <Flex vertical flex={1}>
                                                        <Typography.Text className="-filter-title">
                                                            {t(
                                                                'tools:automatedParams.headers.description',
                                                                {
                                                                    entity: singularEntity,
                                                                },
                                                            )}
                                                        </Typography.Text>
                                                        <TextArea
                                                            name="description"
                                                            placeholder={t(
                                                                'tools:automatedParams.placeholders.descriptionOptional',
                                                            )}
                                                            onChange={
                                                                handleChange
                                                            }
                                                            onBlur={handleBlur}
                                                            value={
                                                                values?.description
                                                            }
                                                            data-test="description"
                                                            rows={1}
                                                            status={
                                                                errors.description
                                                                    ? 'error'
                                                                    : ''
                                                            }
                                                        />
                                                        {errors.description && (
                                                            <Typography.Text
                                                                type="danger"
                                                                style={{
                                                                    fontSize:
                                                                        '14px',
                                                                    marginTop:
                                                                        '4px',
                                                                    display:
                                                                        'block',
                                                                }}
                                                            >
                                                                {
                                                                    errors.description
                                                                }
                                                            </Typography.Text>
                                                        )}
                                                    </Flex>
                                                </Flex>

                                                {type === 'collections' &&
                                                    hasRhAttributionPermission && (
                                                        <Flex
                                                            vertical
                                                            style={{
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <Typography.Text className="-filter-title">
                                                                {t(
                                                                    'tools:automatedParams.headers.methodology',
                                                                )}
                                                            </Typography.Text>
                                                            <Select
                                                                disabled
                                                                value={
                                                                    isEditMode
                                                                        ? value?.isUsingRha ===
                                                                          true
                                                                            ? 'league'
                                                                            : 'team'
                                                                        : rightsholderAttribution ===
                                                                            'league'
                                                                          ? 'league'
                                                                          : 'team'
                                                                }
                                                                options={[
                                                                    {
                                                                        value: 'team',
                                                                        label: t(
                                                                            'tools:automatedParams.methodology.teamRhAttribution',
                                                                        ),
                                                                    },
                                                                    {
                                                                        value: 'league',
                                                                        label: t(
                                                                            'tools:automatedParams.methodology.leagueRhAttribution',
                                                                        ),
                                                                    },
                                                                ]}
                                                                style={{
                                                                    width: '100%',
                                                                }}
                                                                data-test="methodology-select"
                                                            />
                                                        </Flex>
                                                    )}
                                            </Flex>
                                        </Panel>
                                    </Collapse>

                                    {(showAutomatedCollections ||
                                        showAutomatedCampaigns) && (
                                        <AutomatedParamsContainer
                                            values={values}
                                            errors={errors}
                                            setValues={setValues}
                                            setTouched={setTouched}
                                            setErrors={setErrors}
                                            activeParams={activeParams}
                                            setActiveParams={setActiveParams}
                                            logicalOperators={logicalOperators}
                                            setLogicalOperator={
                                                setLogicalOperator
                                            }
                                            groupAssignments={groupAssignments}
                                            onAddGroup={addGroup}
                                            onAddCondition={
                                                addConditionToGroup
                                            }
                                            onRemoveGroup={removeGroup}
                                            onRowActivated={onRowActivated}
                                            type={type}
                                            entityType={type}
                                        />
                                    )}

                                    {status && status.msg && (
                                        <Text
                                            type="danger"
                                            className="text-center"
                                        >
                                            {status.msg}
                                        </Text>
                                    )}
                                </>
                            )}
                        </Form>
                    </Drawer>
                );
            }}
        </Formik>
    );
};

export default connect(
    ['client', 'filters'],
    combineActions({
        getFilters,
    }),
)(AutomatedParamDrawer);
